/**
 * Settings Page
 *
 * Provides tabbed interface for managing:
 * - Profile settings (display name, timezone)
 * - Security settings (password changes)
 * - Target Ranges (parameter monitoring thresholds)
 * - Notification preferences (daily digest, channels)
 */

import React from 'react';
import { Container, Tabs, Title, Stack } from '@mantine/core';
import { IconUser, IconLock, IconTarget, IconBell } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { ProfileForm } from '../components/ProfileForm';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { TargetRangesForm } from '../components/TargetRangesForm';
import { NotificationSettingsForm } from '../components/NotificationSettingsForm';

const VALID_TABS = ['profile', 'security', 'targets', 'notifications'] as const;
type SettingsTab = typeof VALID_TABS[number];

function isValidTab(value: string | null): value is SettingsTab {
  return value !== null && (VALID_TABS as readonly string[]).includes(value);
}

export const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = isValidTab(tabParam) ? tabParam : 'profile';

  const handleTabChange = (value: string | null) => {
    if (value && value !== 'profile') {
      setSearchParams({ tab: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Title order={2}>Settings</Title>

        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
            <Tabs.Tab value="security" leftSection={<IconLock size={16} />}>
              Security
            </Tabs.Tab>
            <Tabs.Tab value="targets" leftSection={<IconTarget size={16} />}>
              Target Ranges
            </Tabs.Tab>
            <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>
              Notifications
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="md">
            <ProfileForm />
          </Tabs.Panel>

          <Tabs.Panel value="security" pt="md">
            <ChangePasswordForm />
          </Tabs.Panel>

          <Tabs.Panel value="targets" pt="md">
            <TargetRangesForm />
          </Tabs.Panel>

          <Tabs.Panel value="notifications" pt="md">
            <NotificationSettingsForm />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};
